/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { toJS } from 'mobx';

import { ApiVersion, EntityIdentifier, SortColumn } from './Entities/CommerceTypes.g';

import {
    BypassCache,
    CacheType,
    ICallerContext,
    IDataServiceQueryInternal,
    IDataServiceRequest,
    IDictionary,
    IPartialCookieContext
} from './Interfaces';
import {
    modelManagersRetailServerRequestError,
    modelManagersRetailServerRequestFinished,
    modelManagersRetailServerRequestStarted,
    Tracer,
    Utils
} from './logging';

import { ProxyError } from './exceptions';
import { ErrorParser } from './exceptions/ErrorParser';
import {
    convertToString,
    EMPTY,
    format,
    hasElements,
    isEmpty,
    isNullOrWhitespace,
    isObjectNotEmptyOrNull
} from './extensions/StringExtensions';

import { IODataRequest, ODataHttpRequest } from './externals/DataJS.New';
export const ODATA_HAS_NEXT_PAGE_PROPERTY = '@odata.nextLink';
export const GET: string = 'GET';
export const POST: string = 'POST';
export const PATCH: string = 'PATCH';
export const DELETE: string = 'DELETE';
export const GET_FROM_RETURNIDENTIFIER: string = '___GET__FROM__RETURNIDENTIIER___';
export const ODATA_TOTAL_COUNT_PROPERTY = '@odata.count';

const ApiTimeoutInMilliSecond = 10000;
const OPERATINGUNITNUMBER_HEADERNAME = 'OUN';
const AUTHORIZATION_HEADERNAME = 'Authorization';
const ACCEPT_LANGUAGE = 'Accept-Language';
const REQUEST_ID = 'RequestId';
const APPSESSION_ID = 'AppSessionId';
const USERSESSION_ID = 'UserSessionId';
const DEFAULT_HEADER: { [headerName: string]: string } = {
    Prefer: 'return=representation'
};
const OBO_REQUEST = 'isOBORequest';
const GET_ACCOUNT_SELECTION_COOKIE = 'getAccountSelectionCookie';
const CUSTOMER_ACCOUNT_NUMBER = 'CAN';

/***
 * Parses the odata result returned from server.
 *
 * @param {any} data The result returned from server.
 * @param {any} resultType The entity type to parse the result as.
 */
export function parseOdataResult(data: any, resultType: any): any {
    if (!data) {
        return data;
    }

    if (!data.value) {
        return resultType ? new resultType(data) : data;
    }

    const resultSet = data.value;

    if (resultType) {
        if (Object.prototype.toString.call(resultSet) === '[object Array]') {
            for (let i = 0; i < resultSet.length; i++) {
                resultSet[i] = new resultType(resultSet[i]);
            }
        }
    }

    // hasNextPage only apply to array which is typeof 'object'
    typeof resultSet === 'object' ? (resultSet.hasNextPage = !(data[ODATA_HAS_NEXT_PAGE_PROPERTY] === undefined)) : null;
    return resultSet;
}

/**
 * Represents a data service request object.
 */
export class DataServiceRequest implements IDataServiceRequest {
    private get batchRequestUri(): string {
        return this._callerContext.requestContext.apiSettings.baseUrl + '/$batch';
    }

    /**
     * Gets Locale for the current data service factory instance.
     */
    public get locale(): string {
        return this._callerContext.requestContext.locale;
    }

    /**
     * Sets Locale for the current data service factory instance.
     */
    public set locale(locale: string) {
        this._callerContext.requestContext.locale = locale;
    }

    /**
     * get cacheKeyFromInput.
     */
    public get cacheKeyFromInput(): string {
        return this._cacheKeyFromInput;
    }

    /**
     * get cacheKeyIdentifierFromReturnType.
     */
    public get cacheKeyIdentifierFromReturnType(): string {
        return this._cacheKeyIdentifierFromReturnType;
    }

    /**
     * get cacheObjectType.
     */
    public get cacheObjectType(): string {
        return this._cacheObjectType;
    }

    /**
     * get cacheKeyFromReturnType.
     */
    public get cacheKeyFromReturnType(): string[] {
        return this._cacheKeyFromReturnType;
    }

    /**
     * get totalCount.
     */
    public get totalCount(): number {
        return this._totalCount;
    }

    /**
     * get query.
     */
    public get query(): IDataServiceQueryInternal {
        return this._query;
    }

    /**
     * Gets query parameters for data service request.
     */
    public queryParams<T>(): T {
        return this._query.data.parameters as T;
    }

    /**
     * Gets Odata properties
     */
    public get otherOdataProperties(): IDictionary<string> {
        return this._otherOdata;
    }

    private _actionId: string;
    private _cacheObjectType: string;
    private _cacheKeyFromInput: string;
    private _cacheKeyFromReturnType: string[] = [];
    private _cacheKeyIdentifierFromReturnType: string;

    private _query: IDataServiceQueryInternal;
    private _callerContext: ICallerContext;
    private _requestUri: string;
    private _method: string;
    private _id: number;
    private _hasOperationName: boolean;
    private _totalCount: number;
    private _otherOdata: IDictionary<string>;

    private static buildBatchRequest(batchRequestUri: string, requests: DataServiceRequest[]): IODataRequest {
        let requestId = 1;

        // Creates an empty batch request.
        const odataBatchRequest: IODataRequest = {
            requestUri: batchRequestUri,
            method: POST,
            data: { __batchRequests: [] }
        };

        const batchRequests = odataBatchRequest.data.__batchRequests;
        let changeRequests: IODataRequest[];

        requests.forEach(request => {
            request._id = requestId++;

            const odataRequest: IODataRequest = {
                requestUri: request._requestUri,
                method: request._method,
                data: DataServiceRequest.toJson(request._query.data),
                headers: { 'Content-ID': request.id(), Prefer: 'return=representation' }
            };

            if (request._method === GET) {
                batchRequests.push(odataRequest);
            } else {
                if (!changeRequests) {
                    changeRequests = [];
                    batchRequests.push({ __changeRequests: changeRequests });
                }
                changeRequests.push(odataRequest);
            }
        });

        return odataBatchRequest;
    }

    /**
     * Parse the batch responses.
     * @param {any[]} batchResponses The server responses.
     * @param {any[]} requests The request objects.
     * @param {any[]} responses The responses passed down to the success callback.
     */
    private static parseBatchResponses(batchResponses: any[], requests: any[], responses: any[]): ProxyError[] {
        let errors: ProxyError[] = new Array();

        batchResponses.forEach(response => {
            if (response.__changeResponses) {
                errors = errors.concat(DataServiceRequest.parseBatchResponses(response.__changeResponses, requests, responses));
            } else {
                if (!response.message) {
                    const requestId = parseInt(response.headers['Content-ID'], 10);

                    responses[requestId] = parseOdataResult(response.data, requests[requestId - 1]._query.returnType);
                } else {
                    errors = errors.concat(ErrorParser.parseErrorMessage(response));
                }
            }
        });

        return errors;
    }

    /**
     * TODO - check if below function can be replaced with JSON.stringify.
     * Helper function to convert data into a stringified JSON object.
     * @param {any} parent The container of the element.
     * @param {any} data The data to be converted to a stringified JSON object.
     * @param {boolean} [modifyDataInPlace] Whether the object passed should be modified in place. If not, a clone is created instead.
     */
    private static toJson(data: any, parent?: any, modifyDataInPlace?: boolean): string {
        let clone = data;

        if (data === null) {
            return null;
        } else if (typeof data !== 'object') {
            // if data type is primitive type, no need to process further
            // as calling jquery extend or calling foreach prop on string type
            // will transform string to array of chars which will be incorrect result.
            return data;
        }

        if (data && data.parameters) {
            data = data.parameters;
            parent = null;
        } else {
            parent = data;
        }

        // clones the data object instead of changing it, if no modifications in place
        if (!modifyDataInPlace) {
            clone = { ...data };
        }

        for (const prop in clone) {
            if (clone.hasOwnProperty(prop)) {
                const property = clone[prop];

                // only non-undefined properties are added to the object
                if (typeof property !== 'undefined') {
                    let value;
                    if (property === null) {
                        value = null;
                    } else if (typeof property === 'object') {
                        if (property instanceof Array) {
                            value = DataServiceRequest.arrayToJson(property, parent, modifyDataInPlace);
                        } else if (property instanceof Date) {
                            value = DataServiceRequest.toDateTimeOffset(property);
                        } else {
                            // recursively stringifies objects
                            value = DataServiceRequest.toJson(property, clone, modifyDataInPlace);
                        }
                    } else {
                        value = property;
                    }

                    clone[prop] = value;
                }
            }
        }

        return clone;
    }

    /**
     * Helper function to convert array into a stringified JSON object following OData specifications.
     * @param {Array} array The data to be converted to a stringified JSON object.
     * @param {any} parent The container of the array.
     * @param {boolean} [modifyDataInPlace] Whether the object passed should be modified in place. If not, a clone is created instead.
     */
    private static arrayToJson(array: any[], parent: any, modifyDataInPlace?: boolean): any {
        const data = [];
        for (let i = 0; i < array.length; i++) {
            data[i] = DataServiceRequest.toJson(array[i], array, modifyDataInPlace);
        }

        // if array is not a top level data on an action
        return data;
    }

    /**
     * Add offset fields to Date object for serialization purposes.
     * @param {Date} dateTime The date.
     * @return {Date} The date with __edmType and __offset fields injected.
     */
    private static toDateTimeOffset(dateTime: Date): Date {
        if (!(dateTime as any).__edmType || !(dateTime as any).__offset) {
            const pad: (val: number) => string = (val: number) => (val < 10 ? '0' + val : val.toString());
            const sign: string = dateTime.getTimezoneOffset() > 0 ? '-' : '+';
            const offset: number = Math.abs(dateTime.getTimezoneOffset());
            const hours: string = pad(Math.floor(offset / 60));
            const minutes: string = pad(offset % 60);

            (dateTime as any).__edmType = 'Edm.DateTimeOffset';
            (dateTime as any).__offset = sign + hours + ':' + minutes;
        }

        return dateTime;
    }

    /**
     * Formats entity key(s) in request Uri.
     * @param {any} key The key dictionary.
     * @return {string} The formated string of the key(s).
     */
    private static formatKey(key: any): string {
        let result: string = EMPTY;

        if (key) {
            const formattedKey = [];
            let propertyName: string = EMPTY;

            for (const property in key) {
                if (key.hasOwnProperty(property)) {
                    propertyName = property;
                    if (typeof key[propertyName] === 'string') {
                        formattedKey.push(propertyName + `='${key[propertyName]}'`);
                    } else {
                        formattedKey.push(propertyName + '=' + key[propertyName]);
                    }
                }
            }

            if (formattedKey.length === 1) {
                if (typeof key[propertyName] === 'string') {
                    result = `'${key[propertyName]}'`;
                } else {
                    result = key[propertyName];
                }
            } else {
                result = formattedKey.join();
            }
        }

        return result;
    }

    /**
     * Formats function parameters in request Uri.
     * @param {any} parameters The parameter dictionary.
     * @return {string} The formated and encoded string of the parameters.
     */
    private static formatFunctionParameter(parameters: any): string {
        let result: string = EMPTY;

        if (parameters) {
            for (const parameter in parameters) {
                if (parameters.hasOwnProperty(parameter)) {
                    const paramValue = parameters[parameter];

                    if (!isEmpty(result)) {
                        result += ',';
                    }

                    if (paramValue === undefined) {
                        // handles undefined value
                        continue;
                    } else if (typeof paramValue === 'string') {
                        // handles string type value
                        result += parameter + `='${encodeURIComponent(paramValue)}'`;
                    } else {
                        // handles null and other types value
                        result += parameter + '=' + encodeURIComponent(paramValue);
                    }
                }
            }
        }

        return result;
    }

    // TODO - use node url api for url parsing
    private static addParameter(requestUri: string, key: string, value: any): string {
        if (value) {
            let parameterSeparator = '?';

            if (requestUri.indexOf(parameterSeparator) > 0) {
                parameterSeparator = '&';
            }

            return requestUri + parameterSeparator + key + '=' + value;
        } else {
            return requestUri;
        }
    }

    private static getActionIdFromInput(query: IDataServiceQueryInternal) {
        return `@msdyn365-commerce/retail-proxy/${query.entitySet}/${query.action}`;
    }

    private static getCacheObjectValueFromReturnType(returnType: any): string {
        // return type can be actual class itself like CategoryClass instead of string 'CategoryClass'
        const cacheObjectTypeValue = typeof returnType === 'function' ? returnType.name : returnType;
        return cacheObjectTypeValue.replace('Class', '');
    }

    private static buildCacheFromInput(query: IDataServiceQueryInternal, cacheKeyIdentifierFromReturnType: string): string {
        // TODO if return type have distinct primary key (and not a collection), cache key for action will be computed primary key

        let cacheKey = {};
        // if query have key like { RecordId: 56873008 }
        query.key && (cacheKey = { ...query.key });
        // parameters are input to function like query.createDataServiceRequestForOperation('GetCategories', true, CategoryClass, 'true', { channelId })
        //     => {channelId} is parameters.
        query.data && query.data.parameters && (cacheKey = { ...cacheKey, ...(query.data.parameters as {}) });
        query.resultSettings &&
            (cacheKey = {
                ...cacheKey,
                ...query.resultSettings.Paging,
                ...query.resultSettings.Sorting
            });
        query.filterSettings && (cacheKey['filter'] = query.filterSettings);
        isObjectNotEmptyOrNull(query.tokens) && (cacheKey['token'] = query.tokens);
        // TODO - add OUN in cache key
        const cacheKeyValue =
            convertToString(cacheKey) ||
            (cacheKeyIdentifierFromReturnType ? GET_FROM_RETURNIDENTIFIER : `${query.entitySet}|${query.entityType}|NOKEY`);
        Tracer.Information(`{CacheKey - CacheValue} : ${cacheKey} - ${cacheKeyValue}`);
        return cacheKeyValue;
    }

    private static getDataCacheType(query: IDataServiceQueryInternal): CacheType {
        // cache all result in request cacheisObjectNotEmptyOrNull
        //
        // Follow following rule for app cache
        // -------------------------------------------------------------------------------------
        //        scenario                                 app cache
        // -------------------------------------------------------------------------------------
        // 1. if returnType is  -                             true
        //          a. Category
        //          b. Product
        //          c. OrgUnit
        //          d. ProductCatalog
        //          e. Warehouse
        if (query.entitySet === 'Carts' && query.action === 'GetMaxLoyaltyPointsToRedeemForTransactionBalance') {
            return 'none';
        }

        return query.options.bypassCache === 'get' || query.options.bypassCache === 'getAndPut' ? 'none' : 'request';
    }

    constructor(query: IDataServiceQueryInternal) {
        Tracer.Information('DataServiceRequest.constructor()');
        this._query = toJS(query, { recurseEverything: true });
        this._hasOperationName = false;

        switch (query.action) {
            case 'Read':
            case 'ReadAll':
                this._method = GET;
                break;

            case 'Create':
                this._method = POST;
                break;

            case 'Update':
                this._method = PATCH;
                break;

            case 'Delete':
                this._method = DELETE;
                break;

            default:
                if (query.isAction) {
                    // Sets the HTTP method to be POST for actions
                    this._method = POST;
                } else {
                    // Sets the HTTP method to be GET for functions
                    this._method = GET;
                }

                this._hasOperationName = true;
        }

        this._actionId = DataServiceRequest.getActionIdFromInput(query);

        this._cacheObjectType = DataServiceRequest.getCacheObjectValueFromReturnType(query.options.returnEntity);
        // TODO - handle Key when it is array like SHIFT case
        const entityIdentifier = this._cacheObjectType === 'SimpleProduct' ? 'Product' : this._cacheObjectType;
        this._cacheKeyIdentifierFromReturnType = EntityIdentifier[entityIdentifier] && EntityIdentifier[entityIdentifier].Key;

        this._cacheKeyFromInput = DataServiceRequest.buildCacheFromInput(this._query, this._cacheKeyIdentifierFromReturnType);
    }

    /**
     * Gets the Request Identifier.
     * Auto-generated unique identifier if executed in batch. Used to fetch the response from batch result array.
     */
    public id(): number {
        return this._id;
    }

    public getActionId = () => this._actionId;

    public getCacheObjectType = () => this._cacheObjectType;

    public getCacheKey = () => this._cacheKeyFromInput;

    public dataCacheType = () => DataServiceRequest.getDataCacheType(this._query);

    public shouldCacheOutput = () => true;

    public overrideCacheType = (bypassCache: BypassCache) => {
        bypassCache && (this._query.options.bypassCache = bypassCache);
    };

    /**
     * Executes the request.
     * @param {any} callerContext The caller context.
     * @return {IAsyncResult<T>} The async result.
     */
    public execute<T>(callerContext: ICallerContext): Promise<T> {
        if (
            !callerContext.requestContext ||
            !callerContext.requestContext.apiSettings ||
            !callerContext.requestContext.apiSettings.oun ||
            !callerContext.requestContext.apiSettings.baseUrl
        ) {
            throw new Error(`CallerContext missing required request parameters - [${JSON.stringify(callerContext.requestContext)}]`);
        }

        if (!callerContext.requestContext.locale) {
            throw new Error(`CallerContext missing locale - [${JSON.stringify(callerContext.requestContext)}]`);
        }

        this._callerContext = callerContext;
        this._requestUri = this.getRequestUri();
        let result: Promise<T>;
        const headers: { [headerName: string]: string } = this.buildQueryHeader();

        if (
            !this._query.isReturnTypeACollection ||
            (this._query.resultSettings &&
                this._query.resultSettings.Paging &&
                this._query.resultSettings.Paging.Top &&
                this._query.resultSettings.Paging.Top >= 0)
        ) {
            result = this.loadPage<T>(headers);
        } else {
            result = this.getAllPages<T>(headers, 0);
        }

        return result;
    }

    // Need to build batch handler ----------- PENDING
    /**
     * Executes the batch requests.
     * @param {DataServiceRequest[]} requests The collection of requests to execute.
     * @param {any} [callerContext] The caller context.
     * @return {IAsyncResult<Array>} The async result.  Responses at index I correlates to request with identifier I.
     */
    public executeBatch(requests: DataServiceRequest[], callerContext?: ICallerContext): Promise<any[]> {
        if (
            !callerContext.requestContext ||
            !callerContext.requestContext.apiSettings ||
            !callerContext.requestContext.apiSettings.oun ||
            !callerContext.requestContext.apiSettings.baseUrl
        ) {
            throw new Error(`CallerContext missing required request parameters - [${JSON.stringify(callerContext.requestContext)}]`);
        }

        if (!callerContext.requestContext.locale) {
            throw new Error(`CallerContext missing locale - [${JSON.stringify(callerContext.requestContext)}]`);
        }

        this._callerContext = callerContext;
        this._requestUri = this.getRequestUri();

        return new Promise((resolve, reject) => {
            ODataHttpRequest(
                DataServiceRequest.buildBatchRequest(this.batchRequestUri, requests),
                (data: any) => {
                    const responses: any[] = new Array();
                    const errors: ProxyError[] = DataServiceRequest.parseBatchResponses(data.__batchResponses, requests, responses);

                    if (errors.length === 0) {
                        Tracer.Information(this.batchRequestUri + ' success');
                        resolve(responses);
                    } else {
                        Tracer.Errors(errors, this.batchRequestUri + ' failed.');
                        reject(errors);
                    }
                },
                (error: any) => {
                    Tracer.Error(this.batchRequestUri + ' failed');
                    reject(ErrorParser.parseErrorMessage(error));
                }
            );
        });
    }

    /**
     * Gets the Request Uri.
     */
    private getRequestUri(): string {
        let requestUri: string = this._callerContext.requestContext.apiSettings.baseUrl + 'Commerce';
        if (this._query.entitySet) {
            requestUri = `${requestUri}/${this._query.entitySet}`;

            if (this._query.key) {
                requestUri = `${requestUri}(${DataServiceRequest.formatKey(this._query.key)})`;
            } else if (this._query.entitySet === 'Customers' && this._query.key !== null && this._query.key !== undefined) {
                requestUri = `${requestUri}('')`;
            }
        }

        // Appends the action (POST) or function (GET) name to the Uri
        if (this._hasOperationName) {
            requestUri += '/' + this._query.action;

            // Construct OData function Url with parameters. [e.g. Func(), Func(param1='',param2='')]
            if (!this._query.isAction && this._query.data && this._query.data.parameters) {
                requestUri += '(' + DataServiceRequest.formatFunctionParameter(this._query.data.parameters) + ')';
            }
        }

        if (this._query.isReturnTypeACollection) {
            requestUri = DataServiceRequest.addParameter(
                requestUri,
                '$top',
                this._query.resultSettings && this._query.resultSettings.Paging
                    ? this._query.resultSettings.Paging.Top
                    : ApiTimeoutInMilliSecond
            ); // Config.defaultPageSize);
            requestUri = DataServiceRequest.addParameter(
                requestUri,
                '$skip',
                this._query.resultSettings && this._query.resultSettings.Paging ? this._query.resultSettings.Paging.Skip : 0
            );
            requestUri = DataServiceRequest.addParameter(requestUri, '$inlinecount', this._query.inlineCount ? 'allpages' : null);

            if (this._query.resultSettings.count) {
                requestUri = DataServiceRequest.addParameter(requestUri, '$count', 'true');
            }

            requestUri = DataServiceRequest.addParameter(
                requestUri,
                '$filter',
                this._query.filterSettings ? this._query.filterSettings : 0
            );

            if (this._query.resultSettings && this._query.resultSettings.Sorting && this._query.resultSettings.Sorting.Columns) {
                const orderByColumns = [];
                this._query.resultSettings.Sorting.Columns.forEach((column: SortColumn) => {
                    if (!isNullOrWhitespace(column.ColumnName || EMPTY)) {
                        orderByColumns.push(format('{0}%20{1}', column.ColumnName, column.IsDescending ? 'desc' : 'asc'));
                    }
                });
                if (orderByColumns.length > 0) {
                    requestUri = DataServiceRequest.addParameter(requestUri, '$orderby', orderByColumns.join(','));
                }
            }
        }

        if (this._query.expands && hasElements(this._query.expands)) {
            requestUri = DataServiceRequest.addParameter(requestUri, '$expand', this._query.expands.join());
        }

        if (!isNullOrWhitespace(ApiVersion)) {
            requestUri = DataServiceRequest.addParameter(requestUri, 'api-version', ApiVersion);
        }

        return requestUri;
    }

    /**
     * Executes paginated requests for all entities.
     * @param {AsyncResult{T}} asyncResult The async result.
     * @param {{ [headerName: string]: string }} headerName the headers for the request.
     * @param {number} skip The offset.
     * @param {Array<T>} The array result.
     */
    private getAllPages<T>(headers: { [headerName: string]: string }, skip: number, results?: T[]): Promise<T> {
        skip = skip || 0;
        const top = 1000; // Config.defaultPageSize;
        this._query.resultSettings = this._query.resultSettings || {};
        this._query.resultSettings.Paging = this._query.resultSettings.Paging || {};
        this._query.resultSettings.Paging.Top = top;
        results = results || [];

        return this.loadPage<T>(headers, skip)
            .then((loadPageResults: T) => {
                if (loadPageResults) {
                    if (typeof (loadPageResults as any).length === 'number') {
                        results = results.concat(loadPageResults);

                        if ((loadPageResults as any).length < top) {
                            // This page contains less items than top
                            if ((loadPageResults as any).hasNextPage) {
                                // This means the pagesize defined on server side is smaller than the top
                                skip += (loadPageResults as any).length;
                                return this.getAllPages<T>(headers, skip, results); // Try to fetch the next page
                            } else {
                                // No more pages need to fetch
                                return Promise.resolve((results as any) as T);
                            }
                        } else {
                            // This page contains exact amount of item as the top, should not contain more
                            skip += top;
                            return this.getAllPages<T>(headers, skip, results); // Try to fetch the next page
                        }
                    } else {
                        // The pageResult is a single item
                        results.push((loadPageResults as unknown) as T);
                        return Promise.resolve((results as unknown) as T);
                    }
                }
            })
            .catch(error => {
                return Promise.reject(error);
            });
    }

    /**
     * Builds the header to be sent as part of the request.
     * @returns {{ [headerName: string]: string }} a key value pair collection of header names and header values.
     */
    private buildQueryHeader(): { [headerName: string]: string } {
        const headers: { [headerName: string]: string } = {};
        headers['from-keystone'] = 'true';

        // copy default values from default header
        for (const headerName in DEFAULT_HEADER) {
            if (DEFAULT_HEADER.hasOwnProperty(headerName)) {
                headers[headerName] = DEFAULT_HEADER[headerName];
            }
        }

        // Copy operating unit number if present for C2 (if an operation is done by a customer or by anonymous user) scenarios.
        if (!isNullOrWhitespace(this._callerContext.requestContext.apiSettings.oun)) {
            headers[OPERATINGUNITNUMBER_HEADERNAME] = this._callerContext.requestContext.apiSettings.oun;
        }

        // Add accept-language header to send user preferred locale to server.
        headers[ACCEPT_LANGUAGE] = this._callerContext.requestContext.locale;

        // Add custom AppInsights headers
        headers[REQUEST_ID] = this._callerContext.requestContext.operationId;

        // OBO request
        const { user, cookies } = this._callerContext.requestContext;
        if (user.hasOwnProperty(OBO_REQUEST)) {
            const isOBORequest = user[OBO_REQUEST];
            if (cookies.hasOwnProperty(GET_ACCOUNT_SELECTION_COOKIE)) {
                const accountSelectionCookie = cookies[GET_ACCOUNT_SELECTION_COOKIE];
                const accountSelectionCustomerId = accountSelectionCookie();
                if (!isNullOrWhitespace(accountSelectionCustomerId) && isOBORequest) {
                    const accountSelectionCustomerIdParts: string[] = accountSelectionCustomerId.split(':');
                    headers[CUSTOMER_ACCOUNT_NUMBER] = accountSelectionCustomerIdParts[1];
                } else if (isOBORequest) {
                    headers[CUSTOMER_ACCOUNT_NUMBER] = "";
                }
            }
        }

        // add any tokens as part of the header
        for (const tokenName in this._query.tokens) {
            if (this._query.tokens.hasOwnProperty(tokenName)) {
                const tokenValue: string = this._query.tokens[tokenName];

                if (!isNullOrWhitespace(tokenValue)) {
                    headers[tokenName] = tokenValue;
                }
            }
        }

        // Now check whether authentication token is present or not, if present it will be added into Authorization header.
        if (
            this._callerContext &&
            this._callerContext.requestContext.user &&
            !isNullOrWhitespace(this._callerContext.requestContext.user.token)
        ) {
            headers[AUTHORIZATION_HEADERNAME] = `id_token ${this._callerContext.requestContext.user.token}`;
        }

        // Check if an Bearer access token is being provided. If present it will be added into Authorization header
        if (
            this._callerContext &&
            this._callerContext.requestContext &&
            !isNullOrWhitespace(this._callerContext.requestContext.accessToken)
        ) {
            headers[AUTHORIZATION_HEADERNAME] = `Bearer ${this._callerContext.requestContext.accessToken}`;
        }

        // Add AppSessionId and UserSessionId to request header
        if (this._callerContext.requestContext[`cookies`]) {
            const cookieData: IPartialCookieContext = this._callerContext.requestContext[`cookies`];
            const appSessionCookie = cookieData.get('ai_session');
            const userSessionCookie = cookieData.get('ai_user');

            if (appSessionCookie && appSessionCookie.status === 'FOUND') {
                const appSessionId = appSessionCookie.value?.split('|')[0];
                headers[APPSESSION_ID] = appSessionId;
            }
            if (userSessionCookie && userSessionCookie.status === 'FOUND') {
                const userSessionId = userSessionCookie.value?.split('|')[0];
                headers[USERSESSION_ID] = userSessionId;
            }
        }

        return headers;
    }

    /**
     * Executes request for one page.
     * @param {AsyncResult<T>} The async result.
     * @param {{ [headerName: string]: string }} headerName the headers for the request.
     */
    private loadPage<T>(headers: { [headerName: string]: string }, skip?: number): Promise<T> {
        if (skip) {
            this._query.resultSettings = this._query.resultSettings || {};
            this._query.resultSettings.Paging = this._query.resultSettings.Paging || {};
            this._query.resultSettings.Paging.Skip = skip;
        }

        const requestId = Utils.generateGuid();
        modelManagersRetailServerRequestStarted(requestId, this._requestUri);

        return new Promise((resolve, reject) => {
            // This is the pipe for all OData API calls.
            ODataHttpRequest(
                {
                    requestUri: this.getRequestUri(),
                    method: this._method,
                    data: DataServiceRequest.toJson(this._query.data),
                    headers
                },
                (data: any) => {
                    modelManagersRetailServerRequestFinished(requestId, this._requestUri);
                    const resultSet = parseOdataResult(data, this._query.returnType);

                    if (data) {
                        if (data[ODATA_TOTAL_COUNT_PROPERTY]) {
                            this._totalCount = data[ODATA_TOTAL_COUNT_PROPERTY];
                        }
                        const otherOdata = DataServiceRequest.setMetadata(data);
                        if (otherOdata) {
                            this._otherOdata = otherOdata;
                        }
                    }

                    if (this._cacheKeyIdentifierFromReturnType) {
                        if (Object.prototype.toString.call(resultSet) === '[object Array]') {
                            this._cacheKeyFromReturnType.concat(
                                resultSet.map(result => result[this._cacheKeyIdentifierFromReturnType]).filter(Boolean)
                            );
                        } else if (typeof resultSet === 'object') {
                            this._cacheKeyFromReturnType.push(resultSet[this._cacheKeyIdentifierFromReturnType]);
                        }
                    }

                    resolve(resultSet);
                },
                (error: any) => {
                    modelManagersRetailServerRequestError(requestId, this._requestUri, error.message);
                    reject(ErrorParser.parseErrorMessage(error));
                }
            );
        });
    }

    /**
     * Sets all the Odata propeties from retail api on DataServiceRequest
     * @param data retail api call data
     */
    private static setMetadata(data: any): IDictionary<string> | undefined {
        // Sets all the Odata properties on DataServiceRequest
        const odataList = Object.keys(data).filter(key => {
            if (key !== ODATA_TOTAL_COUNT_PROPERTY) {
                return key.indexOf('@odata') === 0;
            }
        });

        if (odataList.length > 0) {
            const odata = odataList.reduce(function(newData, key) {
                const newKey = key.replace('@odata.', '');
                newData[newKey] = data[key];
                return newData;
            }, {});

            return odata;
        }
        return undefined;
    }
}
